// src/views/reports/OverdueReportView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { collection, query, onSnapshot, orderBy, doc } from 'firebase/firestore';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Download, CheckCircle, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { exportToCSV } from '../../utils/csvExport';
import JobFormModal from '../../components/crm/JobFormModal'; 

const OverdueReportView = ({ currentUserProfile }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- STATE FOR MODAL SUPPORT ---
    const [employees, setEmployees] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [resources, setResources] = useState([]);
    const [companySettings, setCompanySettings] = useState(null); // orgtime-7c2e4b19 — the job form needs the company's zone (and its other settings)
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    // --- DATA FETCHING ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // 1. Fetch ALL calendar items (we filter in memory)
        const q = query(collection(db, `organizations/${orgId}/jobs`), orderBy('endAt', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setEvents(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); // s6costing-docid: doc id wins over a stale stored `id` field
            setLoading(false);
        });

        // 2. Fetch Helpers
        const unsubEmp = onSnapshot(collection(db, `organizations/${orgId}/employees`), (snap) => 
            setEmployees(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubCust = onSnapshot(collection(db, `organizations/${orgId}/customers`), (snap) => 
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );
        const unsubRes = onSnapshot(collection(db, `organizations/${orgId}/resources`), (snap) => 
            setResources(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        const unsubOrg = onSnapshot(doc(db, 'organizations', orgId), (snap) => setCompanySettings(snap.exists() ? snap.data() : null)); // orgtime-7c2e4b19

        return () => {
            unsubOrg();
            unsubscribe();
            unsubEmp();
            unsubCust();
            unsubRes();
        };
    }, [currentUserProfile]);

    // --- FILTER LOGIC (STRICT ALLOW LIST) ---
    const overdueEvents = useMemo(() => {
        const now = new Date();
        
        // Only allow these specific types. 
        // Note: We include variations like 'Phone Call' vs 'Phone Appointment' just in case.
        const allowedTypes = ['Appointment', 'Phone Call', 'Phone Appointment', 'Follow-up', 'Estimate'];

        return events.filter(event => {
            // 1. STRICT TYPE CHECK: If it's not in our list, it's gone.
            if (!event.eventType || !allowedTypes.includes(event.eventType)) {
                return false;
            }
            
            // 2. Status Check
            if (event.status === 'Completed' || event.status === 'Canceled') return false;

            // 3. Date Check
            const endDate = event.endAt?.toDate ? event.endAt.toDate() : new Date(event.endAt);
            return endDate < now;
        });
    }, [events]);

    const handleRowClick = (event) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
    };

    const handleExport = () => {
        if (!overdueEvents.length) return alert("No data to export");
        const csvData = overdueEvents.map(event => ({
            Type: event.eventType,
            Due_Date: event.endAt?.toDate ? format(event.endAt.toDate(), 'yyyy-MM-dd HH:mm') : 'N/A',
            Customer: event.clientName || 'Unknown',
            Title: event.title,
            Phone: event.customerDetails?.phone || 'N/A'
        }));
        exportToCSV(csvData, `Overdue_Events_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    };

    // --- FIX: REMOVED HARDCODED PERMISSION CHECK ---
    
    if (loading) return <div className="p-10 text-center text-gray-500">Checking calendar...</div>;

    return (
        <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <Link to="/reports" className="p-2 bg-white border rounded-full hover:bg-gray-100">
                        <ArrowLeft size={20} className="text-gray-600" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            Overdue Events
                            <span className="text-sm font-normal bg-orange-100 text-orange-800 px-2 py-0.5 rounded-full border border-orange-200">
                                {overdueEvents.length} Found
                            </span>
                        </h1>
                        <p className="text-gray-500 text-sm">Click any row to resolve (Reschedule or Complete)</p>
                    </div>
                </div>
                <button 
                    onClick={handleExport}
                    className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors shadow-sm"
                >
                    <Download size={18} /> Export CSV
                </button>
            </div>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-orange-50 text-orange-800 text-sm uppercase border-b border-orange-100">
                            <th className="p-4">Type</th>
                            <th className="p-4">Due Date</th>
                            <th className="p-4">Customer</th>
                            <th className="p-4">Details</th>
                            <th className="p-4">Action</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-gray-700">
                        {overdueEvents.length === 0 ? (
                            <tr>
                                <td colSpan="5" className="p-12 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <CheckCircle size={48} className="text-green-500" />
                                        <span className="font-medium">No missed events. Good communications!</span>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            overdueEvents.map(event => (
                                <tr 
                                    key={event.id} 
                                    onClick={() => handleRowClick(event)}
                                    className="border-b hover:bg-blue-50 cursor-pointer transition-colors group"
                                >
                                    <td className="p-4 font-medium">{event.eventType}</td>
                                    <td className="p-4 text-orange-600 font-bold">
                                        {event.endAt?.toDate ? format(event.endAt.toDate(), 'MMM dd, HH:mm') : 'N/A'}
                                    </td>
                                    <td className="p-4">{event.clientName}</td>
                                    <td className="p-4 text-gray-500">{event.title}</td>
                                    <td className="p-4 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="flex items-center gap-1 text-xs font-bold uppercase">
                                            Open <ExternalLink size={12} />
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* --- EVENT EDITOR MODAL --- */}
            {isModalOpen && (
                <JobFormModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    currentUserProfile={currentUserProfile}
                    jobToEdit={selectedEvent}
                    employees={employees}
                    customers={customers}
                    resources={resources}
                    companySettings={companySettings}
                    jobs={events} // Pass full list for conflicts
                    onSave={() => {
                        setIsModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default OverdueReportView;